import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useModules, useEvidenceTags } from '../../hooks/useSupabase';
import { EvidenceTag } from '../../types/types';
import { TagBadge } from './TagDisplay';

interface InlineTagSelectorProps {
  evidenceId: string;
  currentTags: EvidenceTag[];
  onTagsChange?: (tagIds: string[], tags?: EvidenceTag[]) => void;
  allowEdit?: boolean;
}

interface TagOption {
  value: string;
  label: string;
  color: string;
  moduleId: string;
  moduleName: string;
}

/**
 * Inline tag selector with search/typeahead and suggestions
 */
export const InlineTagSelector: React.FC<InlineTagSelectorProps> = ({
  evidenceId,
  currentTags = [],
  onTagsChange,
  allowEdit = true
}) => {
  const { modules, streams, isLoading: modulesLoading } = useModules();
  const { updateTags, removeTag, addTag } = useEvidenceTags();
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Group and filter streams based on search term
  const groupedOptions = useMemo(() => {
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Group streams by module
    const grouped = modules.reduce((acc, module) => {
      const moduleStreams = streams
        .filter(stream => {
          // Must be in this module
          if (stream.module_id !== module.id) return false;
          // Don't show already selected tags
          if (currentTags.some(tag => tag.stream_id === stream.id)) return false;
          // If searching, filter by search term
          if (searchLower) {
            return stream.name.toLowerCase().includes(searchLower) ||
                   module.name.toLowerCase().includes(searchLower);
          }
          return true;
        })
        .map(stream => ({
          value: stream.id,
          label: stream.name,
          color: module.color,
          moduleId: module.id,
          moduleName: module.name
        }));
      
      if (moduleStreams.length > 0) {
        acc.push({
          moduleId: module.id,
          moduleName: module.name,
          moduleColor: module.color,
          options: moduleStreams
        });
      }
      
      return acc;
    }, [] as Array<{
      moduleId: string;
      moduleName: string;
      moduleColor: string;
      options: TagOption[];
    }>);
    
    return grouped;
  }, [searchTerm, streams, modules, currentTags]);

  // Flatten grouped options for keyboard navigation
  const flatOptions = useMemo(() => {
    return groupedOptions.flatMap(group => group.options);
  }, [groupedOptions]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isSearchFocused || flatOptions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedSuggestionIndex(prev => 
            prev < flatOptions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedSuggestionIndex(prev => 
            prev > 0 ? prev - 1 : flatOptions.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedSuggestionIndex >= 0) {
            handleAddTag(flatOptions[selectedSuggestionIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsSearchFocused(false);
          searchInputRef.current?.blur();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchFocused, flatOptions, selectedSuggestionIndex]);

  // Handle clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddTag = async (option: TagOption) => {
    try {
      // Add the tag
      const newTagIds = [...currentTags.map(t => t.stream_id), option.value];
      await updateTags(evidenceId, newTagIds);
      
      // Create the new tag object
      const newTag: EvidenceTag = {
        stream_id: option.value,
        stream_name: option.label,
        module_id: option.moduleId,
        module_name: option.moduleName,
        module_color: option.color
      };
      
      // Update parent component
      const newTags = [...currentTags, newTag];
      onTagsChange?.(newTagIds, newTags);
      
      // Reset search
      setSearchTerm('');
      setSelectedSuggestionIndex(-1);
      searchInputRef.current?.focus();
    } catch (error) {
      console.error('Error adding tag:', error);
    }
  };

  const handleRemoveTag = async (streamId: string) => {
    try {
      await removeTag(evidenceId, streamId);
      const newTags = currentTags.filter(tag => tag.stream_id !== streamId);
      const newTagIds = newTags.map(t => t.stream_id);
      onTagsChange?.(newTagIds, newTags);
    } catch (error) {
      console.error('Error removing tag:', error);
    }
  };

  if (modulesLoading) {
    return <div className="text-sm text-gray-500">Loading tags...</div>;
  }

  if (!allowEdit) {
    return (
      <div className="flex flex-wrap gap-2">
        {currentTags.length > 0 ? (
          currentTags.map(tag => (
            <TagBadge key={tag.stream_id} tag={tag} />
          ))
        ) : (
          <span className="text-sm text-gray-500">No tags assigned</span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Current Tags */}
      {currentTags.length > 0 && (
        <div className="flex flex-wrap gap-2" role="list" aria-label="Current tags">
          {currentTags.map(tag => (
            <TagBadge 
              key={tag.stream_id} 
              tag={tag}
              removable={true}
              onRemove={() => handleRemoveTag(tag.stream_id)}
            />
          ))}
        </div>
      )}
      
      {/* Search Input with Dropdown */}
      <div className="relative">
        <div className="relative">
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setSelectedSuggestionIndex(-1);
            }}
            onFocus={() => setIsSearchFocused(true)}
            placeholder={currentTags.length === 0 ? "Type to add tags..." : "Add more tags..."}
            className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            aria-label="Search and add tags"
            aria-autocomplete="list"
            aria-expanded={isSearchFocused && groupedOptions.length > 0}
            aria-controls="tag-suggestions"
            aria-activedescendant={
              selectedSuggestionIndex >= 0 
                ? `tag-option-${selectedSuggestionIndex}` 
                : undefined
            }
          />
          <svg
            className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        
        {/* Suggestions Dropdown */}
        {isSearchFocused && groupedOptions.length > 0 && (
          <div
            ref={dropdownRef}
            id="tag-suggestions"
            role="listbox"
            className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-96 overflow-auto"
          >
            {!searchTerm && (
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider sticky top-0 bg-white border-b border-gray-100">
                All Available Tags
              </div>
            )}
            {groupedOptions.map((group) => (
              <div key={group.moduleId}>
                {/* Module Header */}
                <div 
                  className="px-3 py-2 text-xs font-semibold uppercase tracking-wider sticky top-0 bg-gray-50 border-b border-gray-200"
                  style={{ 
                    borderLeft: `4px solid ${group.moduleColor}`,
                    color: group.moduleColor 
                  }}
                >
                  {group.moduleName}
                </div>
                {/* Module Options */}
                {group.options.map((option) => {
                  const optionIndex = flatOptions.findIndex(o => o.value === option.value);
                  return (
                    <button
                      key={option.value}
                      id={`tag-option-${optionIndex}`}
                      role="option"
                      aria-selected={optionIndex === selectedSuggestionIndex}
                      onClick={() => handleAddTag(option)}
                      onMouseEnter={() => setSelectedSuggestionIndex(optionIndex)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center ${
                        optionIndex === selectedSuggestionIndex ? 'bg-blue-50' : ''
                      }`}
                      style={{ paddingLeft: '20px' }}
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{option.label}</div>
                      </div>
                      {optionIndex === selectedSuggestionIndex && (
                        <svg className="h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H6a1 1 0 100 2h4.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Help Text */}
      {currentTags.length === 0 && !isSearchFocused && (
        <p className="text-xs text-gray-500">
          Click above or press <kbd className="px-1 py-0.5 text-xs bg-gray-100 rounded">Tab</kbd> to add tags
        </p>
      )}
    </div>
  );
};